import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, Form, Row, Col, Button, Alert } from "react-bootstrap";
import Spinner from "../generics/Spinner";

const TaskAssignmentForm = ({
  assignment,
  templates,
  machines,
  facilities,
  loading,
  error,
  isEdit,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    task_template: "",
    machine: "",
    facility: "",
    start_date: "",
    is_active: true,
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (assignment && isEdit) {
      setFormData({
        task_template: assignment.task_template || "",
        machine: assignment.machine || "",
        facility: assignment.facility || "",
        start_date: assignment.start_date || "",
        is_active: assignment.is_active !== undefined ? assignment.is_active : true,
      });
    }
  }, [assignment, isEdit]);

  useEffect(() => {
    if (formData.task_template && templates.length > 0) {
      const template = templates.find((t) => t.id === parseInt(formData.task_template));
      setSelectedTemplate(template || null);
    } else {
      setSelectedTemplate(null);
    }
  }, [formData.task_template, templates]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear machine/facility when switching targets
    if (name === "machine" && value) {
      setFormData((prev) => ({ ...prev, machine: value, facility: "" }));
    } else if (name === "facility" && value) {
      setFormData((prev) => ({ ...prev, facility: value, machine: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Validate target selection
    if (!formData.machine && !formData.facility) {
      alert("Please select either a machine or a facility");
      return;
    }

    const submitData = {
      task_template: parseInt(formData.task_template),
      machine: formData.machine ? parseInt(formData.machine) : null,
      facility: formData.facility ? parseInt(formData.facility) : null,
      start_date: formData.start_date,
      is_active: formData.is_active,
    };

    onSubmit(submitData);
  };

  // Filter templates based on target type
  const machineTemplates = templates.filter((t) => t.target_type === "MACHINE");
  const facilityTemplates = templates.filter((t) => t.target_type === "FACILITY");

  // Determine which target selector to show based on selected template
  const showMachineSelector = selectedTemplate?.target_type === "MACHINE";
  const showFacilitySelector = selectedTemplate?.target_type === "FACILITY";

  if (loading && isEdit && !assignment) {
    return <Spinner />;
  }

  return (
    <div className="task-assignment-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <Link to="/maintenance/task-assignments" className="text-decoration-none me-2">
            <i className="fa fa-arrow-left"></i>
          </Link>
          <i className="fa fa-link me-2"></i>
          {isEdit ? "Edit Task Assignment" : "New Task Assignment"}
        </h4>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error saving task assignment"}
        </div>
      )}

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-clipboard me-2"></i>
                Select Task Template
              </Card.Header>
              <Card.Body className="pt-4">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Task Template <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="task_template"
                    value={formData.task_template}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a template...</option>
                    {machineTemplates.length > 0 && (
                      <optgroup label="Machine Templates">
                        {machineTemplates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.frequency_display || t.frequency})
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {facilityTemplates.length > 0 && (
                      <optgroup label="Facility Templates">
                        {facilityTemplates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.frequency_display || t.frequency})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Please select a template
                  </Form.Control.Feedback>
                </Form.Group>

                {selectedTemplate && (
                  <Alert variant="info" className="mb-0">
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{selectedTemplate.name}</strong>
                        <br />
                        <small>
                          {selectedTemplate.task_type_display || selectedTemplate.task_type} |{" "}
                          {selectedTemplate.frequency_display || selectedTemplate.frequency}
                          {selectedTemplate.estimated_duration &&
                            ` | ~${selectedTemplate.estimated_duration} min`}
                        </small>
                      </div>
                      <div>
                        <Link
                          to={`/maintenance/task-templates/${selectedTemplate.id}`}
                          className="btn btn-sm btn-outline-info"
                        >
                          View Template
                        </Link>
                      </div>
                    </div>
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {selectedTemplate && (
              <Card className="mb-4">
                <Card.Header className="reports-filter">
                  <i
                    className={`fa ${showMachineSelector ? "fa-cog" : "fa-building"} me-2`}
                  ></i>
                  Select {showMachineSelector ? "Machine" : "Facility"}
                </Card.Header>
                <Card.Body className="pt-4">
                  {showMachineSelector && (
                    <Form.Group>
                      <Form.Label>
                        Machine <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="machine"
                        value={formData.machine}
                        onChange={handleChange}
                        required={showMachineSelector}
                      >
                        <option value="">Select a machine...</option>
                        {machines.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} - {m.location_name || "No location"}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select a machine
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}

                  {showFacilitySelector && (
                    <Form.Group>
                      <Form.Label>
                        Facility <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="facility"
                        value={formData.facility}
                        onChange={handleChange}
                        required={showFacilitySelector}
                      >
                        <option value="">Select a facility...</option>
                        {facilities.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name} - {f.location_name || "No location"}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select a facility
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}
                </Card.Body>
              </Card>
            )}

            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-calendar me-2"></i>
                Schedule Settings
              </Card.Header>
              <Card.Body className="pt-4">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Start Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please select a start date
                  </Form.Control.Feedback>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-toggle-on me-2"></i>
                Status
              </Card.Header>
              <Card.Body className="pt-4">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    label={formData.is_active ? "Active" : "Inactive"}
                  />
                  <Form.Text className="text-muted">
                    Inactive assignments won't generate new tasks
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-info me-2"></i>
                Assignment Summary
              </Card.Header>
              <Card.Body className="pt-3">
                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <th>Template</th>
                      <td>{selectedTemplate?.name || "-"}</td>
                    </tr>
                    <tr>
                      <th>Target</th>
                      <td>
                        {formData.machine
                          ? machines.find((m) => m.id === parseInt(formData.machine))?.name ||
                            "-"
                          : formData.facility
                          ? facilities.find((f) => f.id === parseInt(formData.facility))?.name ||
                            "-"
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Frequency</th>
                      <td>
                        {selectedTemplate?.frequency_display ||
                          selectedTemplate?.frequency ||
                          "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Start Date</th>
                      <td>{formData.start_date || "-"}</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>{formData.is_active ? "Active" : "Inactive"}</td>
                    </tr>
                  </tbody>
                </table>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save me-2"></i>
                        {isEdit ? "Update Assignment" : "Create Assignment"}
                      </>
                    )}
                  </Button>
                  <Link
                    to="/maintenance/task-assignments"
                    className="btn btn-outline-secondary"
                  >
                    <i className="fa fa-times me-2"></i>
                    Cancel
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default TaskAssignmentForm;
